/**
 * SupervisorSignatureBackfillCard
 * =====================================================================
 * One-shot admin migration UI — surfaces notes where a supervisor signed
 * via "Additional signatures" before the Feb 2026 forward fix went live.
 * Those notes are stuck with empty `supervisor_signed_at` even though a
 * valid supervisor signature exists, which holds them up at the billing
 * guardrail and in the pending-cosign audit.
 *
 * Flow:
 *   1. Admin clicks "Scan" → POST /admin/backfill/supervisor-signatures
 *      with dry_run=true, renders the preview list.
 *   2. Admin reviews matches + status changes.
 *   3. Admin clicks "Apply Backfill" → same endpoint with dry_run=false.
 *
 * Safe to re-run: the Motor query excludes notes already supervisor-signed.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileSignature, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupervisorSignatureBackfillCard = () => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    if (dryRun) setLoading(true); else setApplying(true);
    try {
      const resp = await axios.post(
        `${API}/admin/backfill/supervisor-signatures?dry_run=${dryRun}`
      );
      setResult(resp.data);
      if (dryRun) {
        toast.success(`Found ${resp.data.matched} note${resp.data.matched === 1 ? '' : 's'} to fix`);
      } else {
        toast.success(
          `Backfill applied — ${resp.data.applied} note${resp.data.applied === 1 ? '' : 's'} updated, ${resp.data.status_advances} advanced to signed_ready_for_billing/completed`
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally {
      setLoading(false);
      setApplying(false);
    }
  };

  const matches = result?.matches || [];
  const canApply = result?.dry_run && result.matched > 0;

  return (
    <Card className="border-amber-200" data-testid="backfill-supervisor-signatures-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-amber-600" />
          Supervisor Signature Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600">
          Scans notes where the assigned supervisor signed via the generic
          "Additional signatures" path (pre-fix) but whose{' '}
          <code className="bg-slate-100 px-1 rounded">supervisor_signed_at</code> is
          still empty — then mirrors the signature into the dedicated
          supervisor fields and auto-advances status (billable→
          <code className="bg-slate-100 px-1 rounded">signed_ready_for_billing</code>,
          non-billable→<code className="bg-slate-100 px-1 rounded">completed</code>).
          Safe to re-run. Dry-run by default.
        </p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => run(true)}
            disabled={loading || applying}
            data-testid="backfill-scan-btn"
          >
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            {loading ? 'Scanning…' : 'Scan (dry-run)'}
          </Button>
          <Button
            size="sm"
            variant="default"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            disabled={!canApply || applying}
            onClick={() => {
              if (!window.confirm(
                `Apply backfill to ${result.matched} note${result.matched === 1 ? '' : 's'}? ` +
                `${result.status_advances} will auto-advance to signed_ready_for_billing / completed. ` +
                `This is additive — the original additional_signatures entries are preserved.`
              )) return;
              run(false);
            }}
            data-testid="backfill-apply-btn"
          >
            {applying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            {applying ? 'Applying…' : `Apply Backfill${result?.matched ? ` (${result.matched})` : ''}`}
          </Button>
        </div>

        {result && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-1" data-testid="backfill-result-summary">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={result.dry_run ? 'border-slate-400 text-slate-700' : 'border-green-400 text-green-800 bg-green-50'}>
                {result.dry_run ? 'Dry-run' : 'Applied'}
              </Badge>
              <span>Matched: <strong>{result.matched}</strong></span>
              {!result.dry_run && <span>Applied: <strong>{result.applied}</strong></span>}
              <span>Status advances: <strong>{result.status_advances}</strong></span>
            </div>
            {matches.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-slate-700 hover:text-slate-900">
                  View matches ({matches.length})
                </summary>
                <div className="mt-2 max-h-60 overflow-auto">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="text-slate-600 text-left">
                        <th className="px-1 py-0.5 font-semibold">Note ID</th>
                        <th className="px-1 py-0.5 font-semibold">Current</th>
                        <th className="px-1 py-0.5 font-semibold">Sup 1</th>
                        <th className="px-1 py-0.5 font-semibold">Sup 2</th>
                        <th className="px-1 py-0.5 font-semibold">Status →</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {matches.map((m) => (
                        <tr key={m.note_id} className="hover:bg-slate-100">
                          <td className="px-1 py-0.5 font-mono">{(m.note_id || '').substring(0, 8)}…</td>
                          <td className="px-1 py-0.5">{m.current_status || '—'}</td>
                          <td className="px-1 py-0.5">{m.supervisor_1?.signer_name || '—'}</td>
                          <td className="px-1 py-0.5">{m.supervisor_2?.signer_name || '—'}</td>
                          <td className="px-1 py-0.5 font-semibold text-green-700">
                            {m.status_change ? m.status_change.to : '(no change)'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupervisorSignatureBackfillCard;
